/**
 * Where everything sits on the site plan, by code (no image), for each drawing theme
 * (backend plan.SITE_THEMES; drawn by siteThemes.jsx):
 *   floor      - one building: rooms share walls either side of a hall, the entrance at its west end
 *   warehouse  - one building: zones either side of the main forklift aisle, dock doors on the south wall
 *   region     - places spread over a landmass, joined in order by one winding road from the start
 * Every place gets the same shape - {id, name, missions, x, y, w, h (its box: hit area and zoom),
 * spot (where "you" stand), road (index into the layout's travel route)} - so the player, the
 * marker and the zoom work the same for every theme. Units are the SVG's own (VIEW.w x VIEW.h).
 */
export const VIEW = { w: 900, h: 600 };

function buildingLayout(rooms, { x1, x2, y1, y2, hall, gap }) {
  const cols = Math.max(1, Math.ceil(rooms.length / 2));
  const width = (x2 - x1 - gap * (cols - 1)) / cols;
  const hallY = (y1 + y2) / 2;
  const h = hallY - hall - y1 - gap;
  const placed = rooms.map((room, i) => {
    const col = Math.floor(i / 2);
    const top = i % 2 === 0;
    const x = x1 + col * (width + gap);
    const y = top ? y1 : hallY + hall + gap;
    const door = { x: x + width / 2, y: top ? y + h : y };
    return { ...room, x, y, w: width, h, top, door, spot: { x: door.x, y: hallY + (top ? -hall * 0.45 : hall * 0.45) } };
  });
  const corridor = { x1, x2, y: hallY, half: hall };
  const entrance = { x: x1 - 26, y: hallY };
  // The route: the entrance, then every door's point in the hall, west to east.
  const stops = [...new Set(placed.map((r) => r.door.x))].sort((a, b) => a - b);
  const route = [entrance, ...stops.map((x) => ({ x, y: hallY }))];
  placed.forEach((r) => { r.road = route.findIndex((p) => p.x === r.door.x && p.y === hallY); });
  return { rooms: placed, corridor, entrance, route, building: { x1, x2, y1, y2 } };
}

// Region: up to 8 places at hand-picked spots across the landmass (siteThemes.jsx LAND), west to east.
const REGION_SPOTS = [
  { x: 205, y: 395 }, { x: 300, y: 225 }, { x: 430, y: 330 }, { x: 545, y: 195 },
  { x: 640, y: 360 }, { x: 760, y: 235 }, { x: 790, y: 440 }, { x: 470, y: 480 }
];
const REGION_START = { x: 110, y: 470 };

function smooth(points, steps = 10) {
  // Catmull-Rom through the points, sampled - the road curves, and the marker can follow it.
  const out = [];
  for (let i = 0; i < points.length - 1; i += 1) {
    const p0 = points[i - 1] || points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] || p2;
    for (let s = 0; s < steps; s += 1) {
      const t = s / steps;
      const t2 = t * t;
      const t3 = t2 * t;
      const f = (a, b, c, d) => 0.5 * (2 * b + (-a + c) * t + (2 * a - 5 * b + 4 * c - d) * t2 + (-a + 3 * b - 3 * c + d) * t3);
      out.push({ x: f(p0.x, p1.x, p2.x, p3.x), y: f(p0.y, p1.y, p2.y, p3.y) });
    }
  }
  out.push({ ...points[points.length - 1] });
  return out;
}

function regionLayout(rooms) {
  const steps = 10;
  const towns = REGION_SPOTS.slice(0, rooms.length);
  const route = smooth([REGION_START, ...towns], steps);
  const placed = rooms.map((room, i) => {
    const c = towns[i];
    return { ...room, cx: c.x, cy: c.y, x: c.x - 75, y: c.y - 56, w: 150, h: 104, spot: { x: c.x, y: c.y + 4 }, road: (i + 1) * steps };
  });
  return { rooms: placed, entrance: REGION_START, route, start: REGION_START };
}

export const LAYOUTS = {
  floor: (rooms) => buildingLayout(rooms, { x1: 70, x2: 860, y1: 40, y2: 500, hall: 34, gap: 0 }),
  warehouse: (rooms) => buildingLayout(rooms, { x1: 80, x2: 850, y1: 72, y2: 480, hall: 40, gap: 22 }),
  region: regionLayout
};

export function layoutSite(rooms, theme = 'floor') {
  return { theme: LAYOUTS[theme] ? theme : 'floor', ...(LAYOUTS[theme] || LAYOUTS.floor)(rooms) };
}

/** Where "you" stand: a place's spot, or the entrance / start. */
export function spotOf(layout, where) {
  const room = layout.rooms.find((r) => r.id === where);
  return room ? { ...room.spot } : { ...layout.entrance };
}

/** The way from where you are to a place: along the hall or the road, never through a room. */
export function pathBetween(layout, from, to) {
  const start = layout.rooms.find((r) => r.id === from);
  const target = layout.rooms.find((r) => r.id === to);
  const a = start ? start.road : 0;
  const b = target.road;
  const leg = a <= b ? layout.route.slice(a, b + 1) : layout.route.slice(b, a + 1).reverse();
  const points = [spotOf(layout, from), ...leg, spotOf(layout, to)];
  return points.filter((p, i) => i === 0 || Math.abs(p.x - points[i - 1].x) > 0.01 || Math.abs(p.y - points[i - 1].y) > 0.01);
}

/** A point part-way (t: 0..1, by distance) along a path. */
export function pointAlong(points, t) {
  const lengths = points.slice(1).map((p, i) => Math.hypot(p.x - points[i].x, p.y - points[i].y));
  const total = lengths.reduce((a, b) => a + b, 0);
  let left = Math.max(0, Math.min(1, t)) * total;
  for (let i = 0; i < lengths.length; i += 1) {
    if (left <= lengths[i] || i === lengths.length - 1) {
      const f = lengths[i] ? Math.min(1, left / lengths[i]) : 1;
      return { x: points[i].x + (points[i + 1].x - points[i].x) * f, y: points[i].y + (points[i + 1].y - points[i].y) * f };
    }
    left -= lengths[i];
  }
  return { ...points[points.length - 1] };
}

/** The clock time a mission opens at ("23:45. The warehouse hums..." -> "23:45"), like backend plan._clock. */
export function clockOf(lines) {
  for (const line of lines || []) {
    const m = /\b(\d{1,2})[:.](\d{2})\s*([AaPp]\.?[Mm]\.?)?/.exec(String(line));
    if (m) {
      let h = Number(m[1]);
      const min = Number(m[2]);
      const ampm = (m[3] || '').toLowerCase();
      if (ampm.startsWith('p') && h < 12) h += 12;
      if (ampm.startsWith('a') && h === 12) h = 0;
      if (h < 24 && min < 60) return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
    }
  }
  return null;
}
